import type { GuideStep } from './types';

/**
 * Tematización de guías dirigida por datos. El autor (dashboard no-code)
 * guarda props de diseño en `step.style`; aquí las traducimos a las
 * variables CSS `--veo-*` que ya define `GUIDE_STYLES`.
 *
 * Seguridad: solo asignamos *custom properties* sobre el host (valores,
 * nunca selectores ni código). Validamos cada valor; lo inválido se ignora
 * y queda el default del stylesheet. Ningún valor llega a `innerHTML` ni a
 * `eval` — coherente con las reglas del renderer.
 */

/** Overrides del tema oscuro (claro = defaults del stylesheet). */
const DARK_THEME: Record<string, string> = {
  '--veo-bg': '#1f2937',
  '--veo-text': '#f9fafb',
  '--veo-text-secondary': '#d1d5db',
  '--veo-shadow': '0 20px 60px rgba(0, 0, 0, 0.55)',
};

const ALIGN_JUSTIFY: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

/** `align-self` en el contenedor flex del step (para elementos tipo imagen). */
const ALIGN_SELF: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

/** Elementos con estilo propio + qué usan para alinear (text-align vs align-self). */
const ELEMENT_ALIGN_MODE: Record<string, 'text' | 'self'> = {
  title: 'text',
  text: 'text',
  image: 'self',
};

/** Presets de sombra → box-shadow. */
const SHADOW: Record<string, string> = {
  none: 'none',
  soft: '0 4px 14px rgba(0, 0, 0, 0.10)',
  medium: '0 8px 30px rgba(0, 0, 0, 0.18)',
  strong: '0 24px 60px rgba(0, 0, 0, 0.32)',
};

/** Presets de posición del modal → fracción [x, y] del overlay (0..1). */
const PRESET_POS: Record<string, [number, number]> = {
  center: [0.5, 0.5],
  top: [0.5, 0],
  bottom: [0.5, 1],
  left: [0, 0.5],
  right: [1, 0.5],
  'top-left': [0, 0],
  'top-right': [1, 0],
  'bottom-left': [0, 1],
  'bottom-right': [1, 1],
};

/**
 * Acepta colores CSS razonables: hex (#rgb..#rrggbbaa), funcional
 * (rgb/rgba/hsl/hsla) o keyword simple. No interpola en CSS arbitrario:
 * el valor solo se usa como valor de una custom property.
 */
export const COLOR_RE = /^#[0-9a-f]{3,8}$|^(rgb|rgba|hsl|hsla)\([\d.,%/\sdeg]+\)$|^[a-z]{3,20}$/i;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Aplica las props de diseño de `step.style` como variables CSS sobre el
 * host del shadow root. No-op si no hay style. Idempotente.
 */
export function applyDesignVars(host: HTMLElement, style: GuideStep['style']): void {
  if (!style || typeof style !== 'object') return;
  const s = style as Record<string, unknown>;

  if (typeof s.accentColor === 'string' && COLOR_RE.test(s.accentColor.trim())) {
    host.style.setProperty('--veo-primary', s.accentColor.trim());
  }

  // Fuente (del design system del workspace). Sin `<>{}` para no romper el CSS.
  if (
    typeof s.fontFamily === 'string' &&
    s.fontFamily.length <= 200 &&
    !/[<>{}]/.test(s.fontFamily)
  ) {
    host.style.setProperty('--veo-font', s.fontFamily.trim());
  }

  if (s.theme === 'dark') {
    for (const [key, value] of Object.entries(DARK_THEME)) {
      host.style.setProperty(key, value);
    }
  }

  // Colores del tema PERSONALIZADOS (design system): pisan el preset claro/oscuro.
  if (typeof s.background === 'string' && COLOR_RE.test(s.background.trim())) {
    host.style.setProperty('--veo-bg', s.background.trim());
  }
  if (typeof s.textColor === 'string' && COLOR_RE.test(s.textColor.trim())) {
    host.style.setProperty('--veo-text', s.textColor.trim());
  }
  if (typeof s.textSecondaryColor === 'string' && COLOR_RE.test(s.textSecondaryColor.trim())) {
    host.style.setProperty('--veo-text-secondary', s.textSecondaryColor.trim());
  }

  if (typeof s.radius === 'number' && Number.isFinite(s.radius)) {
    host.style.setProperty('--veo-radius', `${clamp(s.radius, 0, 48)}px`);
  }

  if (typeof s.width === 'number' && Number.isFinite(s.width)) {
    host.style.setProperty('--veo-width', `${clamp(s.width, 220, 720)}px`);
  }

  // Alto mínimo de la tarjeta (modal). min-height y no height: el contenido
  // nunca se recorta. Lo setea el resize por arrastre del editor.
  if (typeof s.height === 'number' && Number.isFinite(s.height)) {
    host.style.setProperty('--veo-min-h', `${clamp(s.height, 120, 900)}px`);
  }

  if (s.align === 'left' || s.align === 'center' || s.align === 'right') {
    host.style.setProperty('--veo-actions-justify', ALIGN_JUSTIFY[s.align] as string);
  }

  if (typeof s.padding === 'number' && Number.isFinite(s.padding)) {
    host.style.setProperty('--veo-pad', `${clamp(s.padding, 0, 64)}px`);
  }

  if (typeof s.margin === 'number' && Number.isFinite(s.margin)) {
    host.style.setProperty('--veo-margin', `${clamp(s.margin, 0, 64)}px`);
  }

  if (typeof s.borderWidth === 'number' && Number.isFinite(s.borderWidth)) {
    host.style.setProperty('--veo-border-width', `${clamp(s.borderWidth, 0, 16)}px`);
  }

  if (typeof s.borderColor === 'string' && COLOR_RE.test(s.borderColor.trim())) {
    host.style.setProperty('--veo-border-color', s.borderColor.trim());
  }

  if (typeof s.shadow === 'string' && SHADOW[s.shadow]) {
    host.style.setProperty('--veo-shadow', SHADOW[s.shadow] as string);
  }

  // Posición del modal: libre (posX/posY en 0..1) tiene prioridad sobre el preset.
  let px: number | undefined;
  let py: number | undefined;
  if (typeof s.posX === 'number' && typeof s.posY === 'number') {
    px = clamp(s.posX, 0, 1);
    py = clamp(s.posY, 0, 1);
  } else if (typeof s.overlayPosition === 'string' && PRESET_POS[s.overlayPosition]) {
    [px, py] = PRESET_POS[s.overlayPosition] as [number, number];
  }
  if (px !== undefined && py !== undefined) {
    host.style.setProperty('--veo-pos-x', `${px * 100}%`);
    host.style.setProperty('--veo-pos-y', `${py * 100}%`);
  }

  // Estilo POR ELEMENTO (title/text/image/cta): cada bloque puede sobrescribir
  // alineación, color, tamaño y márgenes de forma independiente del step.
  if (s.elements && typeof s.elements === 'object') {
    const el = s.elements as Record<string, unknown>;
    applyElementVars(host, 'title', el.title);
    applyElementVars(host, 'text', el.text);
    applyElementVars(host, 'image', el.image);
    applyCtaVars(host, el.cta);
  }
}

/**
 * Estilo del BOTÓN (cta): fondo (default = --veo-primary), color del texto,
 * tamaño, redondeo y alineación de la fila de acciones. Personalizable desde
 * el design system del workspace ("Botones") o por guía.
 */
function applyCtaVars(host: HTMLElement, raw: unknown): void {
  if (!raw || typeof raw !== 'object') return;
  const cta = raw as Record<string, unknown>;
  if (typeof cta.align === 'string' && ALIGN_JUSTIFY[cta.align]) {
    host.style.setProperty('--veo-actions-justify', ALIGN_JUSTIFY[cta.align] as string);
  }
  if (typeof cta.background === 'string' && COLOR_RE.test(cta.background.trim())) {
    host.style.setProperty('--veo-cta-bg', cta.background.trim());
  }
  if (typeof cta.color === 'string' && COLOR_RE.test(cta.color.trim())) {
    host.style.setProperty('--veo-cta-color', cta.color.trim());
  }
  if (typeof cta.fontSize === 'number' && Number.isFinite(cta.fontSize)) {
    host.style.setProperty('--veo-cta-size', `${clamp(cta.fontSize, 10, 24)}px`);
  }
  if (typeof cta.radius === 'number' && Number.isFinite(cta.radius)) {
    host.style.setProperty('--veo-cta-radius', `${clamp(cta.radius, 0, 48)}px`);
  }
}

/**
 * Emite las variables CSS de un elemento (`--veo-{prefix}-*`). Cada prop es
 * opcional y se valida; lo inválido se ignora y queda el default del stylesheet.
 */
function applyElementVars(host: HTMLElement, prefix: string, raw: unknown): void {
  if (!raw || typeof raw !== 'object') return;
  const el = raw as Record<string, unknown>;

  if (
    typeof el.align === 'string' &&
    (el.align === 'left' || el.align === 'center' || el.align === 'right')
  ) {
    const mode = ELEMENT_ALIGN_MODE[prefix] ?? 'text';
    const value = mode === 'self' ? (ALIGN_SELF[el.align] as string) : el.align;
    host.style.setProperty(`--veo-${prefix}-align`, value);
  }
  if (typeof el.color === 'string' && COLOR_RE.test(el.color.trim())) {
    host.style.setProperty(`--veo-${prefix}-color`, el.color.trim());
  }
  if (typeof el.fontSize === 'number' && Number.isFinite(el.fontSize)) {
    host.style.setProperty(`--veo-${prefix}-size`, `${clamp(el.fontSize, 8, 72)}px`);
  }
  if (typeof el.width === 'number' && Number.isFinite(el.width)) {
    host.style.setProperty(`--veo-${prefix}-width`, `${clamp(el.width, 10, 100)}%`);
  }
  if (typeof el.radius === 'number' && Number.isFinite(el.radius)) {
    host.style.setProperty(`--veo-${prefix}-radius`, `${clamp(el.radius, 0, 48)}px`);
  }
  if (typeof el.marginTop === 'number' && Number.isFinite(el.marginTop)) {
    host.style.setProperty(`--veo-${prefix}-mt`, `${clamp(el.marginTop, 0, 64)}px`);
  }
  if (typeof el.marginBottom === 'number' && Number.isFinite(el.marginBottom)) {
    host.style.setProperty(`--veo-${prefix}-mb`, `${clamp(el.marginBottom, 0, 64)}px`);
  }
}
